<script>
    import {
        LoadingModal,
        SvelteTable,
        NotificationModal,
        CardLayout,
        CollapsibleTableIcons,
        SendQuoteOrInvoiceToPatientModal,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getAmountInCurrency,
        getNumber,
        getText,
        goToURL,
        getPaymentAmountPaid,
        getFormatDate,
        getFormatDateAndTime,
        isObjectEmpty,
        getDisplayTotal,
        showError,
        showException,
        formatPortalUrls,
        goToDownloadPage,
        getEstimateStatus,
        showPopupNotification,
        openPortalPage,
        getDateForBackend,
        runWithCommunicationsCheck,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        addIcon,
        EmptyGroupDetails,
        InsuranceTypesBypassDisabledComms,
        MaxPaperProcessingFee,
    } from "../../../Utils/utilConstants.svelte";

    import {
        isSufficientTimeToCreateEstimatesOrInvoices,
        getDisplayBillingRuleText,
        getDisplayPaymentDetailsText,
        isHbfWAFullyCoveredWithPatientPayment,
    } from "../helpers/encounterHelper.svelte";

    import {
        billingRuleCalculateTotals,
        syncItemsAfterCalculation,
    } from "../helpers/billingRuleHelper.svelte";

    import { 
        DirectDoctorStatus,
        DirectEncounterStatus,
        PatientPayInsuranceTypes,
    } from "../../../Utils/utilEnums.svelte";

    import DecimalInput from "../../../Components/Common/DecimalInput.svelte";

    import { allGroups } from "../../../stores";

    import { cacheAllGroups } from "../../../Utils/utilCache.svelte";

    import { createEventDispatcher } from "svelte";
    import PatientEncounterQuoteSendDetails from "./PatientEncounterQuoteSendDetails.svelte";

    export let patientEncounterDetails = {},
        quotesData = [],
        latestQuoteData = {},
        latestQuoteDataItems = [],
        latestInvoiceData = {},
        latestInvoiceDataItems = [],
        itemsNotSaved = false,
        isShowSelectBillingRule = false,
        totalPaymentsAmount = Number(0),
        reloadBillingDetails = () => {},
        reloadPatientEncounter = () => {},
        reloadBillingDetailsAndUpdateDefaultItems = async () => {};

    const dispatch = createEventDispatcher();

    $: hasCurrentQuote =
        quotesData.length && quotesData.some((qut) => qut.status !== 9);

    // No need to check billing rule related properties for Cosmetic encounter with free text code
    $: showCreateQuoteBtn =
        !patientEncounterDetails.skipQuote &&
        !isObjectEmpty(latestQuoteData) &&
        !hasCurrentQuote &&
        latestQuoteDataItems.length &&
        (latestQuoteData.billingCodesType == 2 ||
            !isShowSelectBillingRule ||
            latestQuoteData.billingType != null);
            
    $: isQuoteAndSubmittedStatusOrHigher = [3,4,6].includes(patientEncounterDetails.billingStatus);

    $: btnOptions = [
        {
            iconName: "createIcon",
            iconFunction: () => _openCreateQuoteModal(),
            btnText: "Create",
            iFont: addIcon,
            hasTooltip: true,
            tooltip: "Create Quote",
            isHidden: !showCreateQuoteBtn || isQuoteAndSubmittedStatusOrHigher,
        },
    ];

    let isDownloadingQuote = false,
        isRegeneratingQuote = false;

    let isCreateQuoteModalOpen = false,
        creatingQuote = false,
        isSendQuoteNotificationModalOpen = false;

    let isSetEstimatePaperModalOpen = false,
        selectedEstimateID = Number(0),
        isSettingEstimatePaper = false;

    let isDiscardItemsModalOpen = false,
        discardItemsChange = () => {};

    let isCancelQuoteModelOpen = false,
        isCancellingQuote = false;

    let confirmCreateQuoteWithInsufficientTimeModalOpen = false;

    let isChangeEncounterSkipQuote = false;

    let sendQuoteToPatientModalOpen = false,
        selectedQuoteData = {};

    const columns = [
        {
            key: "estimateId",
            title: "ID",
            headerClass: "text-left pl-3 cursor-default w-[5%] rounded-tl-xl",
            class: "text-left px-3 cursor-pointer w-[5%]",
            renderValue: (v) => getNumber(v.id),
        },
        {
            key: "quoteNumber",
            title: "Quote Number",
            headerClass: "text-left pl-3 cursor-default w-[15%]",
            class: "text-left px-3 cursor-pointer w-[15%]",
            renderValue: (v) => getText(v.estimateNumber),
        },
        {
            key: "createDate",
            title: "Create Date",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => _getFormatDate(v.createdDate, v.isExpired),
        },
        {
            key: "expireDate",
            title: "Due Date",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => _getFormatDate(v.expireDate, v.isExpired, true),
        },
        {
            key: "quoteApprove",
            title: "Approved Date",
            headerClass: "text-left pl-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) =>
                _getQuoteApproveDate(v.quoteApprovedDate, v.quoteApproved),
        },
        {
            key: "gapFee",
            title: "Gap Fee",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getAmountInCurrency(v.actualGap),
        },
        {
            key: "rebate",
            title: "Rebate",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getAmountInCurrency(v.rebate),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-default w-[10%]",
            value: (v) =>
                getAmountInCurrency(
                    v.addGST ? (v.actualGap + v.rebate) * 0.1 : 0,
                ),
        },
        {
            key: "processingFee",
            title: "Processing Fee",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.internalProcessingFee ?? 0),
            headerConfig: {
                showTooltip: true,
                tooltipContent: "Internal Processing Fee",
            },
        },
        {
            key: "totalFee",
            title: "Total Fee",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getDisplayTotal(v),
        },
        {
            key: "payments",
            title: "Quote Payments",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: (v) => getPaymentAmountPaid(v.payments),
        },
        {
            key: "totalPayments",
            title: "Total Payments",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            value: () => getAmountInCurrency(totalPaymentsAmount),
        },
        {
            key: "quoteStatus",
            title: "Status",
            headerClass: "text-left px-3 cursor-default w-[10%]",
            class: "text-left px-3 cursor-pointer w-[10%]",
            renderValue: (v) => _getStatus(v.status),
        },
        {
            key: "buttons",
            title: "",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-pointer",
            renderComponent: {
                component: CollapsibleTableIcons,
                props: {
                    icons: [
                        "downloadLinkIcon",
                        "printIcon",
                        "downloadIcon",
                        "reGenerateIcon",
                        "sendToPatientIcon",
                        "deleteQuoteIcon",
                        "goToPortalPageIcon",
                    ],
                    tableType: "Quote",
                    downloadOnlyStatus: 9,
                    // showCancelledBanner: true,
                    cancelledStatus: 9,
                    showPatientInDownload: PatientPayInsuranceTypes.includes(
                        patientEncounterDetails?.patient?.insuranceType,
                    ),
                },
            },
        },
    ];

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "buttons") {
            if (classList.contains("downloadIcon")) downloadQuote(rowData.id);
            else if (classList.contains("reGenerateIcon"))
                reGenerateQuote(rowData);
            else if (className.includes("goToPortalPageIcon"))
                openPortalPage(rowData, patientEncounterDetails.patient);
            else if (classList.contains("downloadLinkIcon"))
                goToEstimate(rowData.id);
            else if (classList.contains("printIcon"))
                _openSetEstimatePaperModal(rowData);
            else if (classList.contains("sendToPatientIcon"))
                _openSendQuoteToPatientModal(rowData);
            else if (classList.contains("deleteQuoteIcon"))
                _openCancelQuoteModel(rowData.id);
        } else if (rowData.id) {
            if (itemsNotSaved)
                openDiscardItemsModal(() => {
                    goToURL(`/Estimates/view/${rowData.id}`);
                });
            else goToURL(`/Estimates/view/${rowData.id}`);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const downloadQuote = (id, isRegenerate) => {
        goToDownloadPage(0, id, isRegenerate);
    };

    const setEstimatePaper = async () => {
        isSettingEstimatePaper = true;

        await apiMethods.putMethod
            .sendPaperEstimate(selectedEstimateID, paperProcessingFee)
            .then((res) => _handleSendPaperEstimate(res))
            .catch((err) => showException(err));

        _closeSetEstimatePaperModal();

        isSettingEstimatePaper = false;
    };

    const reGenerateQuote = async (data) => {
        const { id, doctorId, patientId, patientEncounterId } = data;

        const regenerateObj = {
            type: 0,
            entityId: id,
            doctorId: doctorId,
            patientId: patientId,
            patientEncounterId: patientEncounterId,
        };

        isRegeneratingQuote = true;

        await apiMethods.postMethod
            .regeneratePdfFile(regenerateObj)
            .then((res) => {
                if (res.id) downloadQuote(id, true);
                else
                    showError(
                        "Unable to re-generate this quote PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Unable to re-generate this quote PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isRegeneratingQuote = false;
    };

    const goToEstimate = (id) => {
        // const url = `https://portal.tcihealth.com.au/?e=${id}`;
        const url = formatPortalUrls("e", id);
        window.open(url, "_blank");
    };

    const updateInvoiceBillingDetails = async () => {
        const {
            useAsaCodes,
            oopType,
            billingCodesType,
            calculationAmount,
            billingType,
            patientPaysTotalFee,
            actualGap,
            totalFee,
        } = latestQuoteData;


        if(billingCodesType == 2) {
            //free text items
            //copy items
            var newInvoiceItems = [];
            latestQuoteDataItems.map((item) => {
                var clonedItem = {...item};
                clonedItem.id = 0;
                newInvoiceItems.push(clonedItem);
            });
            latestInvoiceData.freeTextData = [...newInvoiceItems];
            //set the oop type and other properties from invoice same as on quote on invoice the same as the one on quote
            latestInvoiceData = {
                ...latestInvoiceData,
                patientPaysTotalFee,
                totalFee,
                actualGap,
                patientPaysTotalFee,
                calculationAmount,
                billingType,
                oopType
            };
        } else {
            //mbs or asa, copy items and do the gap fee total fee logic

             if (billingCodesType == 1) {
                //asa items
                var newInvoiceItems = [];
                latestQuoteDataItems.map((item) => {
                    if (item.infoType != "Time") newInvoiceItems.push(item);
                });
                latestInvoiceData.asaData = [...newInvoiceItems];
            } 
            else {
                //mbs items
                var newInvoiceItems = [];
                latestQuoteDataItems.map((item) => {
                    if (item.infoType != "Time") newInvoiceItems.push(item);
                });
                latestInvoiceData.mbsData = [...newInvoiceItems];
            }

            if (oopType != null) {
                latestInvoiceData = {
                    ...latestInvoiceData,
                    patientPaysTotalFee,
                    oopType,
                };

                if (oopType == 0) {
                    latestInvoiceData.calculationAmount = patientPaysTotalFee
                        ? totalFee
                        : actualGap;
                    // 5 - total fee, 3 - gap fee
                    latestInvoiceData.billingType = patientPaysTotalFee ? 5 : 3;
                } else {
                    latestInvoiceData = {
                        ...latestInvoiceData,
                        calculationAmount,
                        billingType,
                        oopType,
                        patientPaysTotalFee,
                    };
                }
            }
        }
       
        if (!latestInvoiceData.id) {
            latestInvoiceData = {
                ...latestQuoteData,
                ...latestInvoiceData,
                isLatestQuoteData: false,
                isLatestInvoiceData: true,
            };
            delete latestInvoiceData.id;

            patientEncounterDetails.billingData = [
                ...patientEncounterDetails.billingData,
                { ...latestInvoiceData },
            ];

        } else
            patientEncounterDetails.billingData =
                patientEncounterDetails.billingData.map((bd) => {
                    if (bd.id == latestInvoiceData.id) return latestInvoiceData;
                    else return bd;
                });

        let response;

        const itemsType = billingCodesType == 2 ? "FREE TEXT" : (billingCodesType == 1 ? "ASA" : "MBS")

        await apiMethods.putMethod
            .updatePatientEncounter(patientEncounterDetails, false, `Update ${itemsType} billing details on Invoice after quote is sent`)
            .then(async (res) => {
                const { id, doctorId } = res;
                let latestInvoiceDataObj = res?.billingData.find(
                    (data) => data.isLatestInvoiceData,
                );

                if(billingCodesType == 2)
                    await reloadBillingDetails(); //reload items after transfer for free text codes
                else if (latestInvoiceDataObj?.id) {
                    //for mbs and asa run calculation
                    const { billingType, calculationAmount } =
                        latestInvoiceDataObj;

                    const billingObj = {
                        type: billingType == 1 ? 3 : billingType,
                        amount: billingType == 1 ? 0 : calculationAmount,
                    };

                    let billingObjRes = await billingRuleCalculateTotals(
                        doctorId,
                        id,
                        latestInvoiceDataObj.id,
                        billingObj,
                    );

                    if (billingObjRes.error)
                        showError(billingObjRes.error, undefined, true);
                    else
                        latestInvoiceDataObj = await syncItemsAfterCalculation(
                            billingObjRes.resolve,
                            latestInvoiceDataObj,
                        );

                    res.billingData = res.billingData.map((bd) => {
                        if (bd.id == latestInvoiceDataObj.id)
                            return latestInvoiceDataObj;
                        else return bd;
                    });

                    await apiMethods.putMethod
                        .updatePatientEncounter(res, false, "Save items totals after copied to invoice.")
                        .then((resp) => {
                            if (!resp || resp.error)
                                showError(
                                    resp.error ??
                                        "Failed to sync latest invoice billing data, please refresh the page and try again and contact support if the issue persists.",
                                );
                            else response = resp;
                        });

                    /* await reloadBillingDetailsAndUpdateDefaultItems(
                        false,
                        response,
                    ); */
                }
                else {
                    response = res;
                }
            })
            .catch((err) => showException(err));

        //dispatch("quoteSendToPatient", response);
        dispatch("quoteSendToPatient"); //without response to trigger reload of canceled items
    };

    let customDueDate = latestQuoteData.customDueDate;

    const createQuote = async (sendQuoteAfterCreation = false, customDueDate) => {
        creatingQuote = true;

        if(customDueDate && customDueDate != latestQuoteData.customDueDate) {
            //need to update quote due date
            patientEncounterDetails.billingData.map((bd) => {
                if (bd.id == latestQuoteData.id) {
                    if (customDueDate)
                        bd.customDueDate = getDateForBackend(
                            customDueDate,
                        );

                    return bd;
                } else return bd;
            });
            //update quote due date
            var res = await apiMethods.putMethod.updatePatientEncounter(patientEncounterDetails, false, `Update quote due date before creation`);
            if (!res || res.error) {
                showError(
                    `Failed to update quote due date, please refresh the page and try again and contact support if the issue persists.`,
                );
            }
        }

        await apiMethods.postMethod
            .createEstimateFromBillingItem(latestQuoteData.id)
            .then(async (res) => {
                    await _handleCreateQuoteFromBillingItem(res, sendQuoteAfterCreation);
                    await updateInvoiceBillingDetails(); //update invoice billing details
                }
            )
            .catch((err) => showException(err));

        creatingQuote = false;

        _closeCreateQuoteModal();
    };

    const openDiscardItemsModal = (callback) => {
        if (!callback) closeDiscardItemsModal();

        isDiscardItemsModalOpen = true;

        discardItemsChange = callback;
    };

    const closeDiscardItemsModal = () => (isDiscardItemsModalOpen = false);

    const cancelEstimate = async () => {
        isCancellingQuote = true;

        await apiMethods.putMethod
            .updateEstimateStatus(selectedEstimateID, 9)
            .then((res) => {
                if (res.error) showError(res.error);
                else reloadPatientEncounter();
            })
            .catch((err) => showException(err));

        isCancellingQuote = false;

        _closeCancelQuoteModel();
    };

    const handleToggleSkipQuote = async () => {
        isChangeEncounterSkipQuote = true;

        await apiMethods.putMethod.updatePatientEncounter({
            ...patientEncounterDetails,
            skipQuote: !patientEncounterDetails.skipQuote,
            billingStatus: Math.max(2, patientEncounterDetails.billingStatus),
        });

        await reloadBillingDetails();

        isChangeEncounterSkipQuote = false;
    };

    // HELPER FUNCTIONS
    const _getFormatDate = (date, isExpired, isDateOnly) => {
        if (isExpired) return "Expired";

        if (isDateOnly) return getFormatDate(date);

        return getFormatDateAndTime(date);
    };

    const _getQuoteApproveDate = (date, isApproved) => {
        if (isApproved) return getFormatDate(date);

        return "Not approved";
    };

    const _getStatus = (status) => {
        let statusText, statusColor;

        statusText = getEstimateStatus(status);

        switch (status) {
            case 1:
            case 2:
            case 4:
                statusColor = "#2DBD6F";
                break;
            case 5:
            case 9:
                statusColor = "#E05757";
                break;
            default:
                statusColor = "";
        }

        return `
                <div class="flex justify-start items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const _openCreateQuoteModal = async () => {
        const isSufficientTimeToCreateEstimates =
            isSufficientTimeToCreateEstimatesOrInvoices(
                patientEncounterDetails,
            );
        const isHbfWaFullyCoveredWithPatientPaymentRequest = await isHbfWAFullyCoveredWithPatientPayment(patientEncounterDetails, latestQuoteData);


        if (isSufficientTimeToCreateEstimates && !isHbfWaFullyCoveredWithPatientPaymentRequest) isCreateQuoteModalOpen = true;
        else {
            _closeCreateQuoteModal();

            if(isHbfWAFullyCoveredWithPatientPayment){
                isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen = true;
            } else {
                confirmCreateQuoteWithInsufficientTimeModalOpen = true;
            }
        }
    };

    const _closeCreateQuoteModal = () => {
        isCreateQuoteModalOpen = false;
        confirmCreateQuoteWithInsufficientTimeModalOpen = false;
    };

    const _closeSendQuoteNotificationModal = () =>
        (isSendQuoteNotificationModalOpen = false);

    const _openSetEstimatePaperModal = async (row) => {
        hasInternalProcessingFee = false;
        if (row.internalProcessingFee) {
            paperProcessingFee = row.internalProcessingFee;
            hasInternalProcessingFee = true;
        } else if (row.groupId) {
            if (!$allGroups.length) await cacheAllGroups();
            const group = $allGroups.find((g) => g.id == row.groupId);
            paperProcessingFee =
                group?.paperProcessingFee ??
                EmptyGroupDetails.paperProcessingFee;
        } else paperProcessingFee = EmptyGroupDetails.paperProcessingFee;
        selectedEstimateID = Number(row.id);
        isSetEstimatePaperModalOpen = true;
    };

    const _closeSetEstimatePaperModal = () => {
        selectedEstimateID = Number(0);
        isSetEstimatePaperModalOpen = false;
    };

    const _handleSendPaperEstimate = (res) => {
        if (!res || res.error)
            showError(
                "Failed to request paper copy of quote, please refresh the page and try again and contact support if the issue persists.",
            );
        else if (res.id) {
            downloadQuote(res.id);
            reloadBillingDetails();
        }
    };

    const _handleCreateQuoteFromBillingItem = async (
        res,
        sendQuoteAfterCreation = false,
    ) => {
        if (res.error) showError(res.error);
        else if (!res.id)
            showError(
                "Failed to create quote, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            await reloadBillingDetails();
            if (sendQuoteAfterCreation) {
                _closeCreateQuoteModal();
                _openSendQuoteToPatientModal({
                    ...res,
                    skipDelay: false,
                });
            } 
        }
    };

    const _openCancelQuoteModel = (id) => {
        isCancelQuoteModelOpen = true;
        selectedEstimateID = Number(id);
    };

    const _closeCancelQuoteModel = () => {
        isCancelQuoteModelOpen = false;
        selectedEstimateID = Number(0);
    };

    const _openSendQuoteToPatientModal = (data) => {
        if (
            patientEncounterDetails?.doctor?.status !==
                DirectDoctorStatus.Active &&
            patientEncounterDetails?.doctor?.status !== DirectDoctorStatus.Demo
        )
            return showPopupNotification(
                "Account not active",
                "The encounter doctor is not active. Please enable it to send this quote.",
            );
        if (patientEncounterDetails?.status === DirectEncounterStatus.Archived)
            return showPopupNotification(
                "The encounter is archived",
                "The encounter is archived. Please un-archive it to send this quote.",
            );
        
        if (patientEncounterDetails?.patient?.deceased)
            return showPopupNotification(
                "Patient Deceased",
                "Unable to send quote, patient deceased.",
            );

        //function in function to hold the send action and run the communications check before it
        var sendAction = () => {  
            sendQuoteToPatientModalOpen = true;
            selectedQuoteData = {
                ...data,
                type: "quote",
                patientPhone: patientEncounterDetails.patient?.phone ?? "",
                patientEmail: patientEncounterDetails.patient?.email ?? "",
                patientContacts:
                    patientEncounterDetails.patient?.patientContacts ?? [],
                insuranceType: latestQuoteData.insuranceType,
            };
        }

        if (patientEncounterDetails.disableNotifications &&
            InsuranceTypesBypassDisabledComms.includes(
                patientEncounterDetails.patient.insuranceType,
            )
        ) {
            sendAction();
        }
        else {
            runWithCommunicationsCheck(patientEncounterDetails, sendAction);
        }
        
    };

    const _closeSendQuoteToPatientModal = () => {
        sendQuoteToPatientModalOpen = false;
        selectedQuoteData = {};

    };

    let sendQuote = true;
    let isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen = false;

    let paperProcessingFee,
        hasInternalProcessingFee = false;

    $: isValidPaperProcessingFee =
        paperProcessingFee >= 0 && paperProcessingFee <= MaxPaperProcessingFee;
</script>

<CardLayout title="Quotes" {btnOptions}>
    {#if !hasCurrentQuote}
        {#if !latestQuoteDataItems.length}
            <div class="grid flex-1 h-full card bg-white rounded-box">
                <div
                    class="flex flex-1 items-center gap-x-4 px-2 py-4 w-full lg:flex-row"
                >
                    <p>No quotes created</p>

                    <!-- <div class="flex gap-x-2 items-cent">
                        <p>Skip Quote:</p>
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            checked={patientEncounterDetails.skipQuote}
                            on:click|preventDefault={handleToggleSkipQuote}
                            disabled={isChangeEncounterSkipQuote}
                        />
                    </div> -->
                </div>
            </div>
        {:else}
            <div class="flex flex-1 flex-col gap-y-2 px-2 py-4 w-full">
                <p>No quotes created</p>
                <p>{getDisplayBillingRuleText(latestQuoteData)}</p>
                <p>{getDisplayPaymentDetailsText(latestQuoteData)}</p>
            </div>
        {/if}
    {/if}
    {#if quotesData.length}
        <div class="w-full overflow-x-auto" id="EncounterQuotesTableContainer">
            {#key quotesData.map(r => r.id).join('|')}
                <SvelteTable
                    tableContainerId="EncounterQuotesTableContainer"
                    {columns}
                    rows={quotesData}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    grayOutFunction={(row) => row.status == 9}
                />
            {/key}
        </div>
    {/if}
</CardLayout>

{#if isDownloadingQuote}
    <LoadingModal titleText="Downloading Quote PDF..." />
{/if}

{#if isRegeneratingQuote}
    <LoadingModal titleText="Regenerating Quote PDF..." />
{/if}

<NotificationModal
    title="Confirmation"
    isModalOpen={isCreateQuoteModalOpen}
    isConfirming={creatingQuote}
    closeModal={_closeCreateQuoteModal}
    closeModalFunction={_closeCreateQuoteModal}
    showConfirmBtn={false}
    contentClass={"p-6 flex flex-col"}
    btnOptions={[
        {
            text: sendQuote ? "Create and Send" : "Create",
            loading: creatingQuote,
            function: () => createQuote(sendQuote, customDueDate),
        },
    ]}
>
   <PatientEncounterQuoteSendDetails 
        {latestQuoteData} 
        {patientEncounterDetails} 
        bind:customDueDate={customDueDate}
        bind:sendQuote={sendQuote}>
    </PatientEncounterQuoteSendDetails>
</NotificationModal>

<NotificationModal
    isModalOpen={confirmCreateQuoteWithInsufficientTimeModalOpen}
    submitText="Continue"
    closeModal={_closeCreateQuoteModal}
    confirmAction={createQuote}
    isConfirming={creatingQuote}
    closeModalFunction={_closeCreateQuoteModal}
>
    <p class="leading-6">The patient was asked to update their details.</p>
    <p class="leading-6">Cancel update and continue?</p>
</NotificationModal>

<NotificationModal
    title="Not allowed"
    isModalOpen={isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen}
    closeModal={() => isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen = false }
    showConfirmBtn={false}
    closeModalFunction={() => isHbfWaFullyCoveredWithPatientPaymentRequestModalOpen = false}
>
    <p class="leading-6">HBF (WA) Fully Covered scheme does not permit the patient to be charged a gap or a total fee.</p>
</NotificationModal>

<NotificationModal
    isModalOpen={isSendQuoteNotificationModalOpen}
    closeModal={_closeSendQuoteNotificationModal}
    showConfirmBtn={false}
    title="Done!"
    closeModalFunction={_closeSendQuoteNotificationModal}
>
    Successfully create quote and send it to your patient.
</NotificationModal>

<NotificationModal
    isModalOpen={isSetEstimatePaperModalOpen}
    title="Get paper quote"
    cancelText="Cancel"
    submitText={isSettingEstimatePaper ? "Confirming" : "Confirm"}
    closeModal={_closeSetEstimatePaperModal}
    confirmAction={setEstimatePaper}
    isConfirming={isSettingEstimatePaper}
    closeModalFunction={_closeSetEstimatePaperModal}
    isConfirmDisabled={!isValidPaperProcessingFee}
    modalMaxWidth="max-w-xl"
>
    <div class="flex gap-x-4 items-center">
        {#if hasInternalProcessingFee}
            Change the processing fee to
        {:else}
            Add
        {/if}
        $
        <DecimalInput
            bind:value={paperProcessingFee}
            inputMax={MaxPaperProcessingFee}
            inputClass={isValidPaperProcessingFee ? "w-16" : "input-error w-16"}
        />
        {#if !hasInternalProcessingFee}
            processing fee to the quote and download.
        {/if}
    </div>
    {#if paperProcessingFee > MaxPaperProcessingFee}
        <p class="text-error mt-5">
            The maximum allowed paper processing fee is ${MaxPaperProcessingFee}.
        </p>
    {/if}
</NotificationModal>

<NotificationModal
    isModalOpen={isDiscardItemsModalOpen}
    closeModal={discardItemsChange}
    confirmAction={closeDiscardItemsModal}
    submitText="Cancel"
    cancelText="Discard changes"
    isReverseBtns={true}
    closeModalFunction={closeDiscardItemsModal}
>
    You have unsaved items, do you want to discard all unsaved changes?
</NotificationModal>

<NotificationModal
    isModalOpen={isCancelQuoteModelOpen}
    cancelText="Delete"
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    isConfirming={isCancellingQuote}
    isReverseBtns
    confirmAction={_closeCancelQuoteModel}
    closeModal={cancelEstimate}
    closeModalFunction={_closeCancelQuoteModel}
>
    <p class="text-error">Cancel this quote?</p>
</NotificationModal>

<SendQuoteOrInvoiceToPatientModal
    data={selectedQuoteData}
    bind:isModalOpen={sendQuoteToPatientModalOpen}
    afterSendFunc={async () => {
        _closeSendQuoteToPatientModal();
    }}
/>
